import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCustomQuery } from '../../graphql';
import { WorkspaceAsset } from '../domain';
import { WorkspaceRoutine } from '../../routine';

const CACHE_KEY = ['workspaceasset'];

export const useWorkspaceAsset = (
  // for now, we'll assume that only one workspace works with an Asset
  assetId: string,
  disabled?: boolean
) => {
  const [workspaceAsset, setWorkspaceAsset] = useState<
    WorkspaceAsset | undefined
  >(undefined);
  const queryClient = useQueryClient();

  const filter = {
    asset: { eq: assetId },
  };

  const { data, isLoading } = useCustomQuery({
    query: FIND_WORKSPACE_ASSET,
    cacheKey: CACHE_KEY,
    variables: { 
      filter,
      assetId
    },
    options: {
      enabled: disabled === undefined || !disabled,
    },
  });

  useEffect(() => {
    if (!data) return;

    const workspaceAsset: WorkspaceAsset = data.findWorkspaceAssets.items[0];
    const groupRoutinesData: WorkspaceRoutine[] = data.routine.findGroupRoutinesByAssetId.routines;

    // Create asset with combined media
    const productMedia = workspaceAsset?.asset?.product?.media || [];
    const assetMedia = workspaceAsset?.asset?.media || [];

    const productMediaIds = new Set(productMedia.map((m: any) => m.id));
    const assetOnlyMedia = assetMedia.filter((m: any) => !productMediaIds.has(m.id));

    const assetWithCombinedMedia = {
      ...workspaceAsset.asset,
      media: [...productMedia, ...assetOnlyMedia]
    };

    if (
      workspaceAsset?.groups?.length &&
      groupRoutinesData?.length
    ) {
      // Create a new workspaceAsset with merged group routines and combined media
      const updatedWorkspaceAsset = {
        ...workspaceAsset,
        asset: assetWithCombinedMedia,
        groups: workspaceAsset.groups.map((group) => {
          // Filter routines that belong to this specific group
          const groupRoutines = groupRoutinesData.filter((routine) => {
            // Check if routine.groups contains the current group.id
            return routine.groups?.some((routineGroup) => routineGroup.id === group.id);
          });

          // Return updated group with routines
          return {
            ...group,
            routines: groupRoutines
          };
        })
      };

      setWorkspaceAsset(updatedWorkspaceAsset as WorkspaceAsset);
    } else {
      // No groups or routines to merge, but still merge media
      const updatedWorkspaceAsset = {
        ...workspaceAsset,
        asset: assetWithCombinedMedia
      };
      setWorkspaceAsset(updatedWorkspaceAsset as WorkspaceAsset);
    }

  }, [data]);

  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({ queryKey: CACHE_KEY, exact: false });
  }

  return {
    workspaceAsset,
    isLoading,
    invalidateQuery
  };
};

const FIND_WORKSPACE_ASSET = `
  query findWorkspaceAssets($assetId: ID!, $filter: WorkspaceAssetFilter) {
    routine {
      findGroupRoutinesByAssetId(assetId: $assetId) {
        routines {
          id
          order
          type
          config
          deactivated
          currentVersion {
            id
          }
          groups {
            id
          }
        }
      }
    }
    findWorkspaceAssets(filter: $filter) {
      items {
        id
        asset {
          id
          name
          description
          details
          lastScannedAt
          coverImage {
            id
            name
            extension
            status
            type
            createdAt
            updatedAt
          }
          media {
            id
            name
            type
            extension
            status
            url
            createdAt
            updatedAt
            categories {
              id
              name
            }
          }
          lastAddress
          lastLocation {
            coordinates
          }
          updatedAt
          addons {
            id
            name
          }
          product {
            id
            name
            media {
              id
              name
              type
              extension
              status
              url
              createdAt
              updatedAt
              categories {
                id
                name
              }
            }
            routines {
              id
              order
              type
              config
              deactivated
              currentVersion {
                id
              }
            }
          }
          serialNumber
        }
        anonymousOptions {
          hideReportAndNote
        }
        routineState {
          workspaceRoutineId
          workspaceRoutineVersionId
          state
        }
        workspace {
          id
          type
        }
        routines {
          id
          order
          type
          config
          deactivated
          currentVersion {
            id
          }
        }
        groups {
          id
          name
        }
        routineOrder {
          workspaceRoutineId
          order
        }
      }
    }
  }
`;